import { useCallback, useRef, useState } from 'react';

import { ERROR_CODES, errorMessages, hasErrorCode } from '@/core/api';

import type { PendingProjectInvite } from '../components/SelfServiceSMBSignupModal/types';
import { captureSelfServiceError } from '../components/SelfServiceSMBSignupModal/utils/sentryScrub';

// UI-agnostic completeSMBSignup confirmation gate (ENG-5334 Piece 2). Owns
// only error-code detection + retry/cancel state; each host renders its own
// componentsv2/ConfirmDialog off this hook's state. Serves two structurally
// different call sites (SubscriptionStep's reducer-driven flow and
// PaymentStep's plain-refs/Apollo flow) so it makes no assumptions about
// either host's surrounding state shape.

// Which of the two completeSMBSignup guard codes fired. Both mean
// "provisioning was blocked, let the user choose" but drive different
// dialog copy/actions at the host: pending_project_invite offers "accept
// invite" as an alternative to continuing paid; active_membership_exists
// does not (there's no invite to accept — the pending-invite query would
// just return null for that user), so the host offers "go to your
// project" / "continue paid" instead.
export type PendingInviteGuardCode =
  | typeof ERROR_CODES.SMB_SIGNUP_PENDING_PROJECT_INVITE
  | typeof ERROR_CODES.SMB_SIGNUP_ACTIVE_MEMBERSHIP_EXISTS;

export interface UsePendingInviteGuardOptions<TResult> {
  // Re-submits completeSMBSignup with overridePendingInviteGuard: true.
  // Each call site owns its own request shape (reducer-driven
  // `onCompleteSMBSignup(input)` vs. a raw Apollo `client.mutate`) — retry()
  // only needs the override flag threaded through.
  submit: (overridePendingInviteGuard: boolean) => Promise<TResult>;
  // Fetches the pending invite once the pending_project_invite code fires,
  // so the host can offer "accept invite" (reuse usePendingProjectInvite to
  // build this). Omit at call sites that don't offer that action, e.g. the
  // PaymentStep booking-flow entry point, scoped to "continue paid" only
  // for v1 (acceptInvitation needs an authenticated viewer that flow can't
  // establish — see plan).
  fetchPendingInvite?: () => Promise<PendingProjectInvite | null>;
}

export interface UsePendingInviteGuardResult<TResult> {
  // Which guard code fired, or null when the dialog isn't showing.
  code: PendingInviteGuardCode | null;
  open: boolean;
  // Populated only for the pending_project_invite code, and only when
  // `fetchPendingInvite` was supplied. Always null for
  // active_membership_exists.
  invite: PendingProjectInvite | null;
  inviteLoading: boolean;
  // Set when fetchPendingInvite() rejects; cleared by cancel() or the next
  // checkError() call. The error is also reported via
  // captureSelfServiceError, so this is purely for host-side inline display.
  inviteFetchError: string | null;
  // True while retry()'s submit(true) call is in flight.
  retrying: boolean;
  // Set when a retry() attempt fails; cleared by cancel() or the next
  // checkError() call.
  retryError: string | null;
  // Inspects a caught error for either guard error code. Returns true (and
  // opens the dialog, kicking off the invite fetch when applicable) when
  // one matched — the host should stop its own generic error handling in
  // that case. Returns false when neither code matched, so the host's
  // usual error path should run as normal.
  checkError: (err: unknown) => boolean;
  // Re-submits with overridePendingInviteGuard: true. Resolves with the
  // mutation result and closes the dialog on success. On failure, keeps
  // the dialog open with retryError set for inline display, and rethrows
  // so the host can additionally react (e.g. captureSelfServiceError).
  retry: () => Promise<TResult>;
  // Dismisses the dialog and resets guard state.
  cancel: () => void;
}

interface GuardState {
  code: PendingInviteGuardCode | null;
  invite: PendingProjectInvite | null;
  inviteLoading: boolean;
  inviteFetchError: string | null;
  retrying: boolean;
  retryError: string | null;
}

const INITIAL_STATE: GuardState = {
  code: null,
  invite: null,
  inviteLoading: false,
  inviteFetchError: null,
  retrying: false,
  retryError: null,
};

export function usePendingInviteGuard<TResult>({
  submit,
  fetchPendingInvite,
}: UsePendingInviteGuardOptions<TResult>): UsePendingInviteGuardResult<TResult> {
  const [state, setState] = useState<GuardState>(INITIAL_STATE);

  const checkError = useCallback(
    (err: unknown): boolean => {
      if (hasErrorCode(err, ERROR_CODES.SMB_SIGNUP_PENDING_PROJECT_INVITE)) {
        const code = ERROR_CODES.SMB_SIGNUP_PENDING_PROJECT_INVITE;
        setState({ ...INITIAL_STATE, code, inviteLoading: !!fetchPendingInvite });
        if (fetchPendingInvite) {
          fetchPendingInvite()
            .then((invite) => {
              setState((prev) =>
                prev.code === code ? { ...prev, invite, inviteLoading: false } : prev
              );
            })
            .catch((fetchErr: unknown) => {
              captureSelfServiceError(fetchErr, {
                step: 'pending_invite_guard',
                substep: 'fetch_invite',
              });
              setState((prev) =>
                prev.code === code
                  ? {
                      ...prev,
                      inviteLoading: false,
                      inviteFetchError: errorMessages(fetchErr)[0],
                    }
                  : prev
              );
            });
        }
        return true;
      }
      if (hasErrorCode(err, ERROR_CODES.SMB_SIGNUP_ACTIVE_MEMBERSHIP_EXISTS)) {
        setState({ ...INITIAL_STATE, code: ERROR_CODES.SMB_SIGNUP_ACTIVE_MEMBERSHIP_EXISTS });
        return true;
      }
      return false;
    },
    [fetchPendingInvite]
  );

  // Guards against a rapid double-click on the confirm dialog's button
  // firing two concurrent submit() calls: while a retry is in flight, a
  // second retry() call returns the same in-flight promise instead of
  // calling submit() again.
  const retryPromiseRef = useRef<Promise<TResult> | null>(null);

  const retry = useCallback((): Promise<TResult> => {
    if (retryPromiseRef.current) return retryPromiseRef.current;

    const promise = (async () => {
      setState((prev) => ({ ...prev, retrying: true, retryError: null }));
      try {
        const result = await submit(true);
        setState(INITIAL_STATE);
        return result;
      } catch (err) {
        setState((prev) => ({ ...prev, retrying: false, retryError: errorMessages(err)[0] }));
        throw err;
      } finally {
        retryPromiseRef.current = null;
      }
    })();

    retryPromiseRef.current = promise;
    return promise;
  }, [submit]);

  const cancel = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    code: state.code,
    open: state.code !== null,
    invite: state.invite,
    inviteLoading: state.inviteLoading,
    inviteFetchError: state.inviteFetchError,
    retrying: state.retrying,
    retryError: state.retryError,
    checkError,
    retry,
    cancel,
  };
}
